import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { QzTrayConfiguration, QzTrayConfigurationForm, QzTrayConnectionTestResult } from "../../../types/qztray/QzTrayConfigurationTypes";

/**
 * 🧠 Estado global del módulo QZ Tray Configuration
 */
interface QzTrayConfigurationState {
  configurations: QzTrayConfiguration[];
  activeConfiguration: QzTrayConfiguration | null;
  form: QzTrayConfigurationForm;
  testResult: QzTrayConnectionTestResult | null;
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  createModalOpen: boolean;
  editModalOpen: boolean;
  testModalOpen: boolean;
  selectedConfiguration: QzTrayConfiguration | null;
}

const initialState: QzTrayConfigurationState = {
  configurations: [],
  activeConfiguration: null,
  form: {
    host: "",
    port: 8181,
    protocol: "ws",
    sslEnabled: false,
    timeoutMs: 5000,
    tenantId: "",
    branchId: null,
  },
  testResult: null,
  isLoading: false,
  isSubmitting: false,
  error: null,
  createModalOpen: false,
  editModalOpen: false,
  selectedConfiguration: null,
  testModalOpen: false
};

const qzTrayConfigurationSlice = createSlice({
  name: "qzTrayConfiguration",
  initialState,
  reducers: {
    // 📋 Cargar lista de configuraciones
    setConfigurations(state, action: PayloadAction<QzTrayConfiguration[]>) {
      state.configurations = action.payload;
      state.error = null;
    },

    // 🔍 Establecer configuración activa
    setActiveConfiguration(state, action: PayloadAction<QzTrayConfiguration | null>) {
      state.activeConfiguration = action.payload;
      state.editModalOpen = true;
    },

    // 🧩 Actualizar campo individual del formulario
    updateConfigurationFormField(
      state,
      action: PayloadAction<{ field: keyof QzTrayConfigurationForm; value: any }>
    ) {
      const { field, value } = action.payload;
      state.form = { ...state.form, [field]: value };
    },

    // 💾 Resetear formulario
    resetConfigurationForm(state) {
      state.form = {
        host: "",
        port: 8181,
        protocol: "ws",
        sslEnabled: false,
        timeoutMs: 5000,
        tenantId: "",
        branchId: null,
      };
      state.testResult = null;
    },

    // 🧪 Guardar resultado de test de conexión
    setTestResult(state, action: PayloadAction<QzTrayConnectionTestResult | null>) {
      state.testResult = action.payload;
    },

    // ⚙️ Estados generales
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setSubmitting(state, action: PayloadAction<boolean>) {
      state.isSubmitting = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },

    // 🔁 Modales
    setCreateModalOpen(state, action: PayloadAction<boolean>) {
      state.createModalOpen = action.payload;
    },
    setEditModalOpen(state, action: PayloadAction<boolean>) {
      state.editModalOpen = action.payload;
    },
    setTestModalOpen(state, action: PayloadAction<boolean>) {
      state.testModalOpen = action.payload;
    },

    // ✏️ Configuración seleccionada
    setSelectedConfiguration(state, action: PayloadAction<QzTrayConfiguration | null>) {
      state.selectedConfiguration = action.payload;
    },

    // 🔄 Reiniciar estado completo
    resetQzTrayConfigurationState(state) {
      Object.assign(state, initialState);
    },
  },
});

export const {
  setConfigurations,
  setActiveConfiguration,
  updateConfigurationFormField,
  resetConfigurationForm,
  setTestResult,
  setLoading,
  setSubmitting,
  setError,
  setCreateModalOpen,
  setEditModalOpen,
  setTestModalOpen,
  setSelectedConfiguration,
  resetQzTrayConfigurationState,
} = qzTrayConfigurationSlice.actions;

export default qzTrayConfigurationSlice.reducer;
